import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { buildRiskAnalysisTaskTypeOptions } from './risk-analysis-task-type.helpers';
import { getRiskAnalysisData } from './risk-analysis.client';
import type { RiskAnalysisInitiative, RiskAnalysisPullRequest, RiskAnalysisTask } from './risk-analysis.client.type';
import { getRiskAnalysisCreatorValue, getRiskCategory } from './risk-analysis.helpers';

/**
 * Custom hook to fetch risk analysis data for a portfolio
 * @param {string} portfolioId ID of the portfolio to fetch data for
 * @param {string} key Key indicating which type of risk analysis data to fetch
 * @returns {Object} Object containing the fetched data and loading state
 * @template {T} Type of the returned data
 */
const useRiskAnalysisData = <T = unknown>(portfolioId: string, key: string) => {
  const { data, isFetching, error } = useQuery<T>({
    queryKey: ['risk-analysis-data', portfolioId, key],
    queryFn: () => getRiskAnalysisData(portfolioId, key),
    enabled: !!portfolioId,
  });

  return { data, isFetching, error };
};

/**
 * Custom hook to fetch risk analysis tasks for a portfolio
 * @param {string} portfolioId ID of the portfolio to fetch tasks for
 * @returns {Object} Object containing the fetched task data and loading state
 */
const useRiskAnalysisTasks = (portfolioId: string): { data: RiskAnalysisTask[] | undefined; isFetching: boolean } => {
  return useRiskAnalysisData<RiskAnalysisTask[]>(portfolioId, 'tasks');
};

/**
 * Custom hook to fetch risk analysis initiatives for a portfolio
 * @param {string} portfolioId ID of the portfolio to fetch initiatives for
 * @returns {Object} Object containing the fetched initiative data and loading state
 */
const useRiskAnalysisInitiatives = (
  portfolioId: string,
): { data: RiskAnalysisInitiative[] | undefined; isFetching: boolean } => {
  return useRiskAnalysisData<RiskAnalysisInitiative[]>(portfolioId, 'initiatives');
};

/**
 * Custom hook to fetch risk analysis epics for a portfolio
 * @param {string} portfolioId ID of the portfolio to fetch epics for
 * @returns {Object} Object containing the fetched epic data and loading state
 */
const useRiskAnalysisEpics = (portfolioId: string): { data: RiskAnalysisTask[] | undefined; isFetching: boolean } => {
  return useRiskAnalysisData<RiskAnalysisTask[]>(portfolioId, 'epics');
};

/**
 * Custom hook to fetch risk analysis pull requests for a portfolio
 * @param {string} portfolioId ID of the portfolio to fetch pull requests for
 * @returns {Object} Object containing the fetched pull request data and loading state
 */
const useRiskAnalysisPullRequests = (
  portfolioId: string,
): { data: RiskAnalysisPullRequest[] | undefined; isFetching: boolean } => {
  return useRiskAnalysisData<RiskAnalysisPullRequest[]>(portfolioId, 'change-requests');
};

/**
 * Custom hook to calculate weekly risk scores for a list of tasks
 * @param {RiskAnalysisTask[]} tasks List of tasks to calculate risk scores for
 * @returns {Object[]} Array of objects containing weekly risk scores
 */
const useWeeklyRiskScores = (tasks: RiskAnalysisTask[]): { date: Date; averageScore: number | null }[] => {
  return useMemo(() => {
    const weeklyScores: { [key: string]: number[] } = {};
    const today = new Date();
    const sixMonthsAgo = dayjs(today).subtract(5, 'month').toDate();

    let currentDate = sixMonthsAgo;
    while (currentDate <= today) {
      // Bucket into Sunday-start weeks. `day(0)` is always Sunday (0 = Sunday
      // regardless of locale), unlike `startOf('week')`, which reads the global
      // dayjs locale's weekStart and would silently shift every bucket to Monday
      // if any module set a Monday-start locale (e.g. dayjs.locale('en-gb')).
      const weekKey = dayjs(currentDate).day(0).format('YYYY-MM-DD');
      weeklyScores[weekKey] = [];
      currentDate = new Date(currentDate.setDate(currentDate.getDate() + 7));
    }

    tasks.forEach((task) => {
      if (!task.created_date) {
        return;
      }

      const taskDate = new Date(task.created_date);
      // Sunday-start week bucket (see the note in the seeding loop above).
      const weekKey = dayjs(taskDate).day(0).format('YYYY-MM-DD');

      if (weeklyScores[weekKey]) {
        const riskScore = task.flora_annotations?.risk_score;
        if (riskScore !== undefined && riskScore !== null) {
          weeklyScores[weekKey].push(riskScore);
        }
      }
    });

    const averages = Object.entries(weeklyScores)
      .map(([weekKey, scores]) => ({
        // Parse the YYYY-MM-DD key as a LOCAL date. `new Date('YYYY-MM-DD')`
        // parses as UTC midnight, which renders as the previous day in negative
        // UTC-offset zones (e.g. America/Los_Angeles), shifting the whole bucket.
        date: dayjs(weekKey).toDate(),
        averageScore: scores.length > 0 ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .filter((point) => point.averageScore !== null);

    return averages;
  }, [tasks]);
};

/**
 * Derives the Types filter options from the loaded tasks: one option per
 * distinct source-system work-item type (lowercased `raw_type` key), labeled
 * with the verbatim `raw_type_label` casing when synced and the raw value
 * otherwise, sorted by label.
 *
 * @param {RiskAnalysisTask[]} tasks List of tasks to derive type options from
 * @returns {{ value: string; label: string }[]} Filter options sorted by label
 */
const useRiskAnalysisTaskTypes = (tasks: RiskAnalysisTask[]): { value: string; label: string }[] => {
  return useMemo(() => buildRiskAnalysisTaskTypeOptions(tasks), [tasks]);
};

/**
 * Custom hook to fetch risk analysis epics for filtering
 * @param {RiskAnalysisTask[]} tasks List of tasks to fetch epics for
 * @returns {Object[]} Array of objects containing epic data
 */
const useRiskAnalysisEpicsFilterOptions = (tasks: RiskAnalysisTask[]): { value: string; label: string }[] => {
  return useMemo(
    () =>
      Array.from(new Set((tasks ?? []).map((task) => task.epic?.name)))
        .filter((epic): epic is string => !!epic)
        .sort()
        .map((epic) => ({ value: epic, label: epic })),
    [tasks],
  );
};

/**
 * Custom hook to fetch risk analysis creators for filtering. Tasks without a
 * reporter bucket under the 'No creator' sentinel option so the seeded
 * all-selected state can actually match them — the predicate in
 * useFilteredData derives the same key.
 *
 * @param {RiskAnalysisTask[]} tasks List of tasks to fetch creators for
 * @returns {Object[]} Array of objects containing creator data
 */
const useRiskAnalysisCreators = (tasks: RiskAnalysisTask[]): { value: string; label: string }[] => {
  return useMemo(
    () =>
      Array.from(new Set((tasks ?? []).map(getRiskAnalysisCreatorValue)))
        .sort()
        .map((creator) => ({ value: creator, label: creator })),
    [tasks],
  );
};

/**
 * Custom hook to fetch risk analysis projects ffor filtering
 * @param {RiskAnalysisTask[]} tasks List of tasks to fetch projects for
 * @returns {Object[]} Array of objects containing project data
 */
const useRiskAnalysisProjectOptions = (tasks: RiskAnalysisTask[]): { value: string; label: string }[] => {
  return useMemo(
    () =>
      Array.from(new Set((tasks ?? []).map((task) => task.project.name)))
        .sort()
        .map((project) => ({ value: project, label: project })),
    [tasks],
  );
};

/**
 * Custom hook to fetch risk analysis initiatives for filtering
 * @param {RiskAnalysisTask[]} tasks List of tasks to fetch initiatives for
 * @returns {Object[]} Array of objects containing initiative data
 */
const useRiskAnalysisInitiativesOptions = (tasks: RiskAnalysisTask[]): { value: string; label: string }[] => {
  return useMemo(() => {
    const uniqueInitiatives = new Map();
    (tasks ?? []).forEach((task) => {
      (task.initiatives ?? []).forEach((initiative) => {
        uniqueInitiatives.set(initiative.id, initiative);
      });
    });

    return Array.from(uniqueInitiatives.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((initiative) => ({
        value: initiative.id,
        label: initiative.name,
      }));
  }, [tasks]);
};

/**
 * Custom hook to get risk categories for filtering
 * @param {RiskAnalysisTask[]} tasks List of tasks to get risk categories from
 * @returns {Object[]} Array of objects containing risk category data
 */
const useRiskAnalysisRiskCategories = (tasks: RiskAnalysisTask[]): { value: string; label: string }[] => {
  return useMemo(() => {
    const categories = new Set(tasks.map((task) => getRiskCategory(task.flora_annotations?.risk_score).label));
    return Array.from(categories)
      .sort()
      .map((category) => ({
        value: category,
        label: category,
      }));
  }, [tasks]);
};

/**
 * Custom hook to get unique Bloomfilter stages from tasks for filtering
 * @param {RiskAnalysisTask[]} tasks List of tasks to extract BF stages from
 * @returns {Object[]} Array of filter options including an 'Unmapped' option for null stages
 */
const useRiskAnalysisBloomfilterStages = (tasks: RiskAnalysisTask[]): { value: string; label: string }[] => {
  return useMemo(() => {
    const stages = Array.from(new Set((tasks ?? []).map((task) => task.bloomfilter_stage ?? 'Unmapped'))).sort();

    return stages.map((stage) => ({
      value: stage,
      label: stage,
    }));
  }, [tasks]);
};

/**
 * Custom hook to get repository options from pull requests
 * @param {RiskAnalysisPullRequest[]} pullRequests List of pull requests to get repositories from
 * @returns {Object[]} Array of objects containing repository data
 */
const useRiskAnalysisRepositoryOptions = (
  pullRequests: RiskAnalysisPullRequest[],
): { value: string; label: string }[] => {
  return useMemo(() => {
    const repositories = new Set(pullRequests.map((pr) => pr.repository.name));
    return Array.from(repositories)
      .sort()
      .map((repository) => ({
        value: repository,
        label: repository,
      }));
  }, [pullRequests]);
};

export {
  useRiskAnalysisBloomfilterStages,
  useRiskAnalysisCreators,
  useRiskAnalysisEpics,
  useRiskAnalysisEpicsFilterOptions,
  useRiskAnalysisInitiatives,
  useRiskAnalysisInitiativesOptions,
  useRiskAnalysisProjectOptions,
  useRiskAnalysisPullRequests,
  useRiskAnalysisRepositoryOptions,
  useRiskAnalysisRiskCategories,
  useRiskAnalysisTasks,
  useRiskAnalysisTaskTypes,
  useWeeklyRiskScores,
};
